import React, {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";
import {
  MessageThreadType,
  SuggestSubjectOrTitleQuery,
  SuggestSubjectOrTitleQueryVariables,
} from "gql/graphql";
import useBurstQuery from "modules/hooks/use-burst-query";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import spinnerAIJson from "modules/layouts/animations/spinner-ai.json";
import twinklingAIStarsJson from "modules/layouts/animations/twinkling-ai-stars.json";
import SUGGEST_SUBJECT_OR_TITLE_QUERY from "modules/messaging/_networking/suggest-subject-or-title";
import {
  MIXPANEL_EVENT_TYPE,
  useMixpanel,
} from "modules/providers/mixpanel-provider/mixpanel-provider";
import dynamic from "next/dynamic";
import FloatingLabel from "modules/_shared/floating-label";

const Lottie = dynamic(() => import("react-lottie-player"), {
  ssr: false,
});

interface IProps {
  draftId: string;
  autoFocus?: boolean;
}

const TEMPLATE_REGEX = /{{(did=\w+|uid=\w+|tid=\w+)}}/g;

const ComposeMessageThreadSubject = ({
  draftId,
  autoFocus = false,
}: IProps) => {
  const { track } = useMixpanel();

  const { draft, setSubject } = useMessageThreadDraft({ draftId });

  /*https://teamcake.atlassian.net/browse/SYN-1485*/
  /*For some reason unless an internal state is used, the cursor jumps to the end*/
  const [_subject, _setSubject] = useState<string>();

  /*feels like another hack but needed when buffering the subject with a local variable*/
  useEffect(() => {
    //if the draft is empty, set the subject to empty, i.e. when the draft is cleared
    if (draft?.subject === "") {
      _setSubject("");
    }
  }, [_subject, draft?.subject]);

  const [burstSuggestSubjectOrTitleQuery, { loading: isSuggestSubjectLoading }] =
    useBurstQuery<
      SuggestSubjectOrTitleQuery,
      SuggestSubjectOrTitleQueryVariables
    >(SUGGEST_SUBJECT_OR_TITLE_QUERY);

  const [isHoveringSubjectSuggestor, setIsHoveringSubjectSuggestor] =
    useState(false);
  const [hasSuggestSuggestorBeenClicked, setHasSuggestSuggestorBeenClicked] =
    useState(false);

  const showSuggestSubject = useMemo(() => {
    const text = draft?.text ?? "";
    const textWithOutCallouts = text.replace(TEMPLATE_REGEX, "");
    return textWithOutCallouts.length > 10;
  }, [draft?.text]);

  const onSubjectChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      _setSubject(event.target.value);
      setSubject(event.target.value);
    },
    [setSubject],
  );

  const requestSuggestSubject = useCallback(async () => {
    if (isSuggestSubjectLoading) {
      return;
    }

    setHasSuggestSuggestorBeenClicked(true);
    setSubject("");
    try {
      const { data } = await burstSuggestSubjectOrTitleQuery({
        fetchPolicy: "no-cache",
        variables: {
          input: {
            message: draft?.text ?? "",
            quotedMessageIdList:
              draft?.quotedMessageList?.map(
                (quotedMessage) => quotedMessage.messageId,
              ) ?? [],
            messageThreadType: draft?.type ?? MessageThreadType.Thread,
            ...(draft?.isLocked !== undefined
              ? { isLocked: draft.isLocked }
              : {}),
            ...(draft?.type === MessageThreadType.Thread &&
            draft?.createMessageUserIdList !== undefined
              ? {
                  createMessageUserIdList: draft.createMessageUserIdList,
                }
              : {}),
            ...(draft?.type === MessageThreadType.Task &&
            draft?.task !== undefined
              ? {
                  priorityType: draft.task.priorityType,
                  assignedUserId: draft.task.assignedUserId,
                  dueTime: draft.task.dueTime,
                }
              : {}),
          },
        },
      });
      setSubject(data?.suggestSubjectOrTitle ?? "");

      track(MIXPANEL_EVENT_TYPE.AUTO_SUGGEST_SUBJECT, {
        "Suggested Subject Method": "Compose",
      });
    } catch {
      // No-op: failures are handled by leaving subject blank.
    }
  }, [
    burstSuggestSubjectOrTitleQuery,
    draft?.createMessageUserIdList,
    draft?.isLocked,
    draft?.quotedMessageList,
    draft?.task,
    draft?.text,
    draft?.type,
    isSuggestSubjectLoading,
    setSubject,
    track,
  ]);

  const placeholder = useMemo(() => {
    switch (draft?.type) {
      case MessageThreadType.Thread: {
        return "Subject";
      }
      case MessageThreadType.Chat: {
        return "Chat Name";
      }
      case MessageThreadType.Task: {
        return "Add Task Title";
      }
      default: {
        return "Subject";
      }
    }
  }, [draft?.type]);

  if (!draft || draft.type === MessageThreadType.Chat) {
    return null;
  }

  return (
    <VStack spacing={0} alignItems="flex-start" w="full">
      <InputGroup>
        {isSuggestSubjectLoading && (
          <InputLeftElement pointerEvents="none" marginTop="20px">
            <Lottie
              play
              animationData={spinnerAIJson}
              style={{ width: 20, height: 20 }}
            />
          </InputLeftElement>
        )}
        <FloatingLabel label={placeholder}>
          <Input
            id="subject"
            variant="outline"
            fontSize={18}
            /*Initializes the value to the draft's subject */
            value={_subject || draft.subject || ""}
            maxLength={60}
            onChange={onSubjectChanged}
            _placeholder={{
              color: colors.placeholderText,
              fontWeight: 400,
              fontStyle: "italic",
            }}
            autoFocus={autoFocus}
          />
        </FloatingLabel>
      </InputGroup>
      {showSuggestSubject && (
        <Popover
          placement="bottom-start"
          isOpen={isHoveringSubjectSuggestor && !hasSuggestSuggestorBeenClicked}
        >
          <PopoverTrigger>
            <Button
              variant="unstyled"
              leftIcon={
                <Lottie
                  play={isHoveringSubjectSuggestor}
                  animationData={twinklingAIStarsJson}
                  style={{ width: 16, height: 16 }}
                />
              }
              iconSpacing={1}
              sx={colors.aiPurpleGradient}
              bg="transparent"
              fontSize={14}
              fontWeight={700}
              disabled={isSuggestSubjectLoading}
              onMouseEnter={() => setIsHoveringSubjectSuggestor(true)}
              onMouseLeave={() => setIsHoveringSubjectSuggestor(false)}
              onClick={requestSuggestSubject}
            >
              {draft?.type === MessageThreadType.Thread
                ? "Suggest Subject"
                : "Suggest Title"}
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent bgColor={colors.blackSecondary} maxW={250} p="4px">
              <PopoverArrow bgColor={colors.blackSecondary} />
              <PopoverBody fontSize={14} color={colors.white} fontWeight={400}>
                <HStack>
                  <Box
                    color={colors.white}
                    bgGradient={colors.aiPurpleGradient.background}
                    px="4px"
                    py="2px"
                    borderRadius="4px"
                  >
                    <Text fontSize={10} fontWeight={600}>
                      New!
                    </Text>
                  </Box>
                  <Text>Use AI to suggest a subject</Text>
                </HStack>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    </VStack>
  );
};

export default memo(ComposeMessageThreadSubject);
